import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import PageLayout from "../../../shared/ui/layouts/PageLayout/PageLayout";
import { ImmotionAuftragSection } from "../../../features/work-order";
import { WorkOrderStatusControl } from "../../../features/work-order-status";
import {
  useWorkOrderDetail,
  getWorkOrderStatusLabel,
  getWorkOrderStatusColor,
  isFollowUpRequired,
} from "../../../entities/work-order";
import { routes } from "../../../shared/config/routes";
import { AppointmentsSection } from "./ui/AppointmentsSection";
import { ContactAttemptsSection } from "./ui/ContactAttemptsSection";
import { BadgeCell } from "./ui/BadgeCell";
import { AuditLogFeed } from "../../../widgets/audit-log";
import { UpsertAppointmentModal, useUpsertAppointment } from "../../../features/appointment-upsert";
import { useAuthStore } from "../../../features/auth/model/store";
import { useIsMobile } from "../../../shared/hooks/useIsMobile";
import { hasAnyRole } from "@myapp/contract";

const ADMIN_ROLES: import('@myapp/contract').UserRole[] = ['MANAGER', 'DIRECTOR', 'SUPERUSER'];

export const WorkOrderDetailPage: React.FC = () => {
  const { workOrderId } = useParams<{ workOrderId: string }>();
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const { data, isLoading, isError } = useWorkOrderDetail(workOrderId);
  const { openEdit } = useUpsertAppointment();

  // The one way to a new Termin: pick the slot on the board, with the whole day's workload
  // visible. The form is a click away there ("Details").
  const handlePlanOnBoard = () => {
    if (!workOrderId) return;
    navigate(routes.disposition.planAppointment(workOrderId));
  };

  const isFollowUp = data ? isFollowUpRequired(data.status) : false;
  // Setting the status by hand overrides what the appointments say, so it is the office's alone —
  // the API enforces the same roles.
  const canChangeStatus = Boolean(user && hasAnyRole(user.roles ?? [], ADMIN_ROLES));

  // A Folgetermin is just another appointment on the same work order — only the label differs.
  const planLabel = isFollowUp ? "Folgetermin planen" : "Termin planen";
  const showActions = Boolean(data);

  const pageTitle = (
    <>
      Auftrag <em style={{ fontWeight: 400 }}>Nr. {workOrderId}</em>
      {data &&
        (canChangeStatus && workOrderId ? (
          // The status in the title is the control that changes it — see `WorkOrderStatusMenu`.
          <span className={isMobile ? "ml-2 align-middle" : "ml-2"}>
            <WorkOrderStatusControl
              workOrderId={workOrderId}
              status={data.status}
              compact={isMobile}
            />
          </span>
        ) : // Spelled out at heading size the status doubles the length of the title, which on a phone
        // costs a whole line. A badge says the same thing at a size the heading can carry.
        isMobile ? (
          <span className="ml-2 align-middle">
            <BadgeCell
              color={getWorkOrderStatusColor(data.status)}
              label={getWorkOrderStatusLabel(data.status)}
            />
          </span>
        ) : (
          <span style={{ color: getWorkOrderStatusColor(data.status) }}>
            {" "}{getWorkOrderStatusLabel(data.status)}
          </span>
        ))}
    </>
  );

  return (
    <PageLayout
      pageTitle={pageTitle}
      scrollable
      back
      backTo={routes.task.root}
      primaryAction={showActions ? {
        label: planLabel,
        onClick: handlePlanOnBoard,
      } : undefined}
    >
      {isLoading && (
        <p className="mt-6 text-gray-400">Lade...</p>
      )}

      {isError && (
        <p className="mt-6 text-red-500">Auftrag konnte nicht geladen werden.</p>
      )}

      {data && (
        <div className="flex flex-col gap-4 md:gap-[20px] mt-4 md:mt-6 overflow-auto">
          {/* Right under the title it explains: the status is „Nicht erreicht" (or its end,
              „Gelöscht"), and this is where the attempts and the comment live. */}
          {workOrderId && (data.status === "NOT_REACHED" || data.status === "CANCELLED") && (
            <ContactAttemptsSection workOrderId={workOrderId} workOrder={data} canEdit={canChangeStatus} />
          )}
          <ImmotionAuftragSection
            immotionAuftragId={workOrderId ?? ""}
            readonly
          />
          <AppointmentsSection
            appointments={data.appointments}
            gewerkText={data.external.gewerkeText}
            onEdit={openEdit}
          />
          {user && hasAnyRole(user.roles ?? [], ADMIN_ROLES) && workOrderId && (
            <AuditLogFeed workOrderId={workOrderId} />
          )}
        </div>
      )}

      <UpsertAppointmentModal
        workOrderId={workOrderId}
      />
    </PageLayout>
  );
};
